import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { AuthTokens } from '../types';

// Базовый URL бэкенда
export const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:5321/api/v1'
    : (window.location.hostname === 'new.eazzy.pro' || window.location.hostname === 'eazzy.pro'
        ? 'https://api.eazzy.pro/api/v1'
        : 'https://api-eazzy.pomuc.ru/api/v1');

class ApiService {
  private api: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (error?: any) => void;
  }> = [];

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor для добавления токена
    this.api.interceptors.request.use(
      (config) => {
        const tokens = this.getTokens();
      
        if (tokens?.accessToken) {
          config.headers.Authorization = `Bearer ${tokens.accessToken}`;
         
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor для обработки 401 ошибок
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        if (error.response?.status === 401 && !originalRequest._retry) {
          
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return this.api(originalRequest);
              })
              .catch((err) => Promise.reject(err));
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const tokens = this.getTokens();
            if (tokens?.accessToken && tokens?.refreshToken) {
              const response = await this.refreshToken(tokens.accessToken, tokens.refreshToken, this.isManager());
              const newTokens = response.data;
              this.setTokens(newTokens);

              // Обрабатываем очередь неудачных запросов
              this.failedQueue.forEach(({ resolve }) => {
                resolve(newTokens.accessToken);
              });
              this.failedQueue = [];

              originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            this.clearTokens();
            this.failedQueue.forEach(({ reject }) => {
              reject(refreshError);
            });
            this.failedQueue = [];
            throw refreshError;
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  public getTokens(): AuthTokens | null {
    try {
      // Сначала проверяем отдельные токены
      const accessToken = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (accessToken) {
        return { accessToken, refreshToken: refreshToken || '' };
      }

      // Затем проверяем auth_user
      const authUserStr = localStorage.getItem('auth_user');
      if (authUserStr) {
        const authUser = JSON.parse(authUserStr);
        if (authUser.accessToken) {
          return { 
            accessToken: authUser.accessToken, 
            refreshToken: authUser.refreshToken || '' 
          };
        }
      }

      // Наконец проверяем старый формат auth_tokens
      const authTokensStr = localStorage.getItem('auth_tokens');
      if (authTokensStr) {
        const authTokens = JSON.parse(authTokensStr);
        if (authTokens.accessToken) {
          return authTokens;
        }
      }

      return null;
    } catch (error) {
      console.error('Error getting tokens:', error);
      return null;
    }
  }

  public setTokens(tokens: AuthTokens): void {
    try {
      // Сохраняем в отдельные поля для consistency
      localStorage.setItem('access_token', tokens.accessToken);
      if (tokens.refreshToken) {
        localStorage.setItem('refresh_token', tokens.refreshToken);
      }
      
      // Также обновляем auth_user если он существует
      const authUserStr = localStorage.getItem('auth_user');
      if (authUserStr) {
        const authUser = JSON.parse(authUserStr);
        authUser.accessToken = tokens.accessToken;
        if (tokens.refreshToken) {
          authUser.refreshToken = tokens.refreshToken;
        }
        localStorage.setItem('auth_user', JSON.stringify(authUser));
      }
      
    } catch (error) {
      console.error('Error setting tokens:', error);
    }
  }

  public clearTokens(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_tokens');
    localStorage.removeItem('user_type');
  }

  public getUserType(): string {
    // Сначала проверяем auth_user для определения типа
    const authUserStr = localStorage.getItem('auth_user');
    if (authUserStr) {
      try {
        const authUser = JSON.parse(authUserStr);
        if (authUser.type) {
          return authUser.type;
        }
      } catch (error) {
        console.error('Error parsing auth_user:', error);
      }
    }
    
    // Fallback на отдельное поле
    return localStorage.getItem('user_type') || 'user';
  }

  public isManager(): boolean {
    return this.getUserType() === 'manager';
  }

  private async refreshToken(accessToken: string, refreshToken: string, isManager: boolean = false): Promise<AxiosResponse<AuthTokens>> {
    const endpoint = isManager ? '/manager/auth/refresh' : '/auth/refresh';
    return axios.post(`${API_BASE_URL}${endpoint}`, {
      accessToken,
      refreshToken,
    });
  }

  // Методы для работы с API
  public async get<T>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return this.api.get(url, config);
  }

  public async post<T>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.api.post(url, data, config);
  }

  public async put<T>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.api.put(url, data, config);
  }

  public async patch<T>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.api.patch(url, data, config);
  }

  public async delete<T>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return this.api.delete(url, config);
  }

  public isAuthenticated(): boolean {
    const tokens = this.getTokens();
    return !!(tokens?.accessToken);
  }

  public logout(): void {
    this.clearTokens();
  }

  public setUserType(userType: 'user' | 'manager'): void {
    localStorage.setItem('user_type', userType);
    
    // Также обновляем auth_user если он существует
    const authUserStr = localStorage.getItem('auth_user');
    if (authUserStr) {
      try {
        const authUser = JSON.parse(authUserStr);
        authUser.type = userType;
        localStorage.setItem('auth_user', JSON.stringify(authUser));
      } catch (error) {
        console.error('Error updating user type in auth_user:', error);
      }
    }
  }
}

export const apiService = new ApiService();
export default apiService;